/**
 * Registro data-driven de RECETAS DE CRAFTEO.
 *
 * Una receta convierte inputs (materiales del inventario) en un output, gastando la cantidad
 * indicada y otorgando XP de una skill de procesamiento. Cada receta pertenece a un
 * `stationType` (banco/estación que la craftea): el jugador abre la ventana de crafteo de una
 * estación y ve las recetas de ese `stationType`.
 *
 * Diseñado genérico para servir CARPINTERÍA ahora (madera → caña) y HERRERÍA después
 * (horno: mena+carbón → lingote; fragua/yunque: lingote → equipo). Sumar recetas = sumar
 * entradas acá, sin tocar el server/cliente del crafteo.
 *
 * Ver docs/CRAFTING_SYSTEM.md y docs/FISHING_SKILL.md.
 */

import type { SkillId } from '../types/skills';
import { FISH_SPECIES } from './fish';
import { fishItemId } from './fish';

export interface CraftingInput {
  /** Item consumido. */
  itemId: string;
  /** Cantidad consumida. */
  qty: number;
}

export interface CraftingRecipe {
  /** ID de la receta (recipe_*). */
  id: string;
  /** Estación que la craftea (carpentry_bench, futuro smithing_furnace/anvil). */
  stationType: string;
  /** Item producido. */
  outputItemId: string;
  /** Cantidad producida por crafteo. */
  outputQty: number;
  /** Materiales consumidos. */
  inputs: CraftingInput[];
  /** Skill de procesamiento que gana XP y gatea la receta. */
  skill: SkillId;
  /** Nivel de `skill` requerido para craftear. */
  requiredLevel: number;
  /** XP otorgada al craftear. */
  xp: number;
  /**
   * Si true, la receta puede QUEMARSE (fallar): al craftear hay una chance de arruinar el
   * output (consume los inputs, no da item, XP mínima). La chance baja a mayor nivel sobre el
   * requerido. Estilo OSRS Cooking. La calcula el server (CraftingHandlers). Default false.
   */
  burnable?: boolean;
}

/**
 * Chance de quemado de una receta `burnable` según el nivel del jugador. Arranca en 40% al
 * nivel mínimo y baja ~2% por nivel por encima, con piso 5%. No-burnable → 0.
 */
export function burnChanceFor(recipe: CraftingRecipe, level: number): number {
  if (!recipe.burnable) return 0;
  const over = Math.max(0, level - recipe.requiredLevel);
  return Math.max(0.05, 0.4 - over * 0.02);
}

export const RECIPES: Record<string, CraftingRecipe> = (function build(): Record<string, CraftingRecipe> {
  const out: Record<string, CraftingRecipe> = {};

  // ── CARPINTERÍA: cañas de pesca (1 por pez, con la madera 1:1 del escalón) ──
  // La carpintería escala con el escalón del pez (sardina lvl 1 → atún lvl 90): crafteás las
  // cañas bajas para levelear y destrabar las altas.
  for (const f of FISH_SPECIES) {
    const id = `recipe_rod_${f.species}`;
    out[id] = {
      id,
      stationType: 'carpentry_bench',
      outputItemId: `tool_fishing_rod_${f.species}`,
      outputQty: 1,
      inputs: [{ itemId: f.wood, qty: 3 }],
      skill: 'skill_carpentry',
      requiredLevel: f.fishLevel,
      xp: 20 + f.fishLevel * 2,
    };
  }

  // ── COCINA: pescado crudo → pescado asado (fogón) ──
  // 1 pescado crudo → 1 asado. Nivel de Cocina = escalón del pez; puede QUEMARSE (burnable).
  for (const f of FISH_SPECIES) {
    const id = `recipe_cook_${f.species}`;
    out[id] = {
      id,
      stationType: 'cooking_fire',
      outputItemId: `food_cooked_${f.species}`,
      outputQty: 1,
      inputs: [{ itemId: fishItemId(f.species), qty: 1 }],
      skill: 'skill_cooking',
      requiredLevel: f.fishLevel,
      xp: 18 + f.fishLevel * 2,
      burnable: true,
    };
  }

  return out;
})();

export function getRecipe(id: string): CraftingRecipe | undefined {
  return RECIPES[id];
}

/** Recetas de una estación (para armar su ventana de crafteo). */
export function getRecipesForStation(stationType: string): CraftingRecipe[] {
  return Object.values(RECIPES).filter((r) => r.stationType === stationType);
}
